import React, {useEffect, useReducer, useState} from 'react'
import PropTypes from 'prop-types';
import {makeStyles} from "@material-ui/core";
import Workspace from "components/Workspace";
import Page from "components/Page";
import RegistrationService from "services/RegistrationService";
import Loading from "components/Loading";
import InfoEntry from "./InfoEntry";
import moment from "moment";
import DateFormats from "constants/DateFormats";
import {RegistrationStatusLabel} from "../constants/RegistrationStatus";
import GridBlock from "components/GridBlock";
import Form from "components/Form";
import VS from 'constants/ValidationShema';
import PartsTable from "components/PartsTable";
import Tr from "translations/Translations";
import {connect} from "react-redux";
import BoldTypography from "components/BoldTypography";
import Button from "components/Button";
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import MultiButton, {createButton} from "components/popup/components/MultiButton";
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import RemoveCircleIcon from '@material-ui/icons/RemoveCircle';
import iconWorksReducer from "libs/iconWorksReducer";
import OrganizationInput from "components/OrganizationInput";
import RouterPath from "libs/RouterPath";
import useSnackbar from "hooks/useSnackbar";
import {simpleConfirm} from "libs/ConfirmNotification";
import {getUserFullNameWithEmailAndDeputy} from "libs/userNameGetter";
import UserRegistrationRejectPopup from "./UserRegistrationRejectPopup";
import UserRegistrationAddOrganizationUnit from "./UserRegistrationAddOrganizationUnit";


const useStyles = makeStyles(theme => ({
    root: {
        padding: theme.spacing(2)
    },
    margin: {
        marginLeft: theme.spacing(2),
        marginRight: theme.spacing(2)
    },
    column: {
        flex: "1 0 50%",
        padding: theme.spacing(2),
        paddingBottom: 0
    },
    backGrid: {
        '&>*:first-child': {
            marginRight: "auto"
        }
    },
    attributes: {
        marginLeft: theme.spacing(4)
    },
    back: {
        position: "absolute"
    }

}))

function UserRegistration(
    {
        match,
        formSubmit
    }
) {

    const classes = useStyles();
    const FORM_ID = "registration_form"
    const regId = match.params.id;
    const [registration, setRegistration] = useState();
    const [availableOrganizations, setAvailableOrganizations] = useState([]);
    const [rejectPopup, setRejectPopup] = useState(false);
    const [addOrgPopup, setAddOrgPopup] = useState(false);
    const {enqueueSuccessSnackbar, enqueueErrorSnackbar} = useSnackbar();
    const [iconWorks, dispatchIconWorks] = useReducer(iconWorksReducer, {
        anyWorks: false,
        accept: false,
        reject: false
    });

    const handleSubmit = () => formSubmit[FORM_ID]();

    useEffect(() => {
        RegistrationService.getRegistration(regId).then((resp) => {
            setRegistration(resp);
            if (resp && resp.editable && !resp.emailAlreadyUsed) {
                RegistrationService.getAvailableOrgs().then(orgs => setAvailableOrganizations(orgs))
            } else {
                setAvailableOrganizations([]);
            }

        })

    }, [regId])

    const goToList = () => {
        RouterPath.redirect(RouterPath.createPath(RouterPath.admin.ADMIN_REGISTRATIONS_LIST))
    }

    const getInitValues = () => {
        const initVal = {};
        const curRoles = registration.form.roles || [];
        const roles = registration.availableRoles.reduce((prev, cur) => {
            prev["_" + cur.identity] = curRoles.includes(cur.identity);
            return prev;
        }, {})
        initVal.roles = roles;

        const attrKeys = Object.keys(registration.availableAttributes);
        for (let attrKey of attrKeys) {
            const selected = registration.form.attributes[attrKey] || [];
            const initAttr = registration.availableAttributes[attrKey].reduce((prev, cur) => {
                prev[cur] = selected.includes(cur);
                return prev;
            }, {})
            initVal[attrKey] = initAttr;
        }
        initVal.organization = registration.form.organization
        return initVal;
    }


    const getButtons = () => {
        const buttons = [];
        buttons.push(createButton(Tr('back_to_list'), goToList, 'outlined', iconWorks.anyWorks, undefined, false, ArrowBackIosIcon));
        buttons.push(createButton(Tr('reject'), onReject, 'outlined', !registration.editable || iconWorks.anyWorks, undefined, iconWorks.reject, RemoveCircleIcon));
        buttons.push(createButton(Tr('accept'), handleSubmit, undefined, registration.emailAlreadyUsed || !registration.editable || iconWorks.anyWorks, undefined, iconWorks.accept, CheckCircleIcon));
        return buttons;
    }

    const onSubmit = (values) => {

        simpleConfirm("registration_form_accept_confirm", () => {
            const {organization, roles, ...rest} = values;
            const form = {
                orgId: organization.orgId,
                roles: Object.keys(roles).filter(k => roles[k] === true).map(k => parseInt(k.substring(1)))
            }
            const attributes = {}
            for (let key of Object.keys(rest)) {
                attributes[key] = Object.keys(rest[key]).filter(k => rest[key][k] === true)
            }
            form.attributes = attributes;

            dispatchIconWorks({type: 'accept', works: true})
            RegistrationService.acceptRegistration(regId, form).then(resp => {
                if (resp && resp.success) {
                    enqueueSuccessSnackbar(Tr("registration_form_accepted"));
                    goToList();
                } else {
                    enqueueErrorSnackbar(Tr('unknown_error_message'))
                    dispatchIconWorks({type: 'accept', works: false})
                }
            }).catch(() => {
                enqueueErrorSnackbar(Tr('unknown_error_message'));
                dispatchIconWorks({type: 'accept', works: false})
            });
        })
    }

    const onReject = () => {
        setRejectPopup(true);
    }
    const doReject = (values) => {
        setRejectPopup(false);
        dispatchIconWorks({type: 'reject', works: true})
        RegistrationService.rejectRegistration(regId, values).then(resp => {
            if (resp && resp.success) {
                enqueueSuccessSnackbar(Tr("registration_form_rejected"));
                goToList();
            } else {
                enqueueErrorSnackbar(Tr('unknown_error_message'))
                dispatchIconWorks({type: 'reject', works: false})
            }
        }).catch(() => {
            enqueueErrorSnackbar(Tr('unknown_error_message'));
            dispatchIconWorks({type: 'reject', works: false})
        })
    }

    const selectOrganization = (formikProps, org) => {
        formikProps.setFieldValue("organization", org);
        setAvailableOrganizations([...availableOrganizations, org])
        setAddOrgPopup(false)
    }

    return (<Page className={classes.root}>
        <Workspace>
            <GridBlock justify="space-between">
                <Button className={classes.back} variant="outlined" Icon={ArrowBackIosIcon} onClick={goToList}
                        disabled={iconWorks.anyWorks}>{Tr("back_to_list")}</Button>
                <BoldTypography variant="h5">{Tr("registration_form")}</BoldTypography>
                <div></div>
            </GridBlock>
            {(!registration || !availableOrganizations) ? <Loading/> :
                <>
                    {!!registration.emailAlreadyUsed && <>
                        <BoldTypography color="error">{Tr("registration_email_already_used")}</BoldTypography>
                        {!!registration.editable && <Button Icon={RemoveCircleIcon} onClick={onReject} disabled={iconWorks.anyWorks}>{Tr("reject")}</Button>}
                    </>}

                    <GridBlock itemClassName={classes.column}>
                        <div>
                            <InfoEntry label="firstName">{registration.form.firstName}</InfoEntry>
                            <InfoEntry label="email">{registration.form.email}</InfoEntry>
                            <InfoEntry label="organizational_unit">{registration.form.organizationName}</InfoEntry>
                            <InfoEntry label="regon">{registration.form.regon}</InfoEntry>
                            <InfoEntry label="city">{registration.form.city}</InfoEntry>
                        </div>
                        <div>
                            <InfoEntry label="lastName">{registration.form.lastName}</InfoEntry>
                            <InfoEntry label="phone">{registration.form.phone}</InfoEntry>
                            <InfoEntry label="nip">{registration.form.nip}</InfoEntry>
                            <InfoEntry label="address">{registration.form.address}</InfoEntry>
                            <InfoEntry label="zip_code">{registration.form.postCode}</InfoEntry>
                        </div>
                    </GridBlock>

                    <InfoEntry className={classes.margin} label="notes">{registration.form.notes}</InfoEntry>
                    <InfoEntry className={classes.margin}
                               label="date_of_receipt">{moment(registration.form.dateInserted).format(DateFormats.DATETIME_FORMAT)}</InfoEntry>
                    <InfoEntry className={classes.margin}
                               label="status">{registration.form.status ? RegistrationStatusLabel(registration.form.status) : null}</InfoEntry>
                    {!!registration.form.decisionDate &&
                        <InfoEntry className={classes.margin}
                                   label="date_of_decision">{moment(registration.form.decisionDate).format(DateFormats.DATETIME_FORMAT)}</InfoEntry>
                    }
                    {!!registration.form.decisionUser &&
                        <InfoEntry className={classes.margin}
                                   label="decision_user">{getUserFullNameWithEmailAndDeputy(registration.form.decisionUser)}</InfoEntry>
                    }
                    <Form
                        id={FORM_ID}
                        initialValues={getInitValues()}
                        validationSchema={VS.object({
                            organization: VS.anyRequired(),
                            roles: VS.lazy((roles) => {
                                const index = Object.keys(roles).findIndex(r => roles[r] === true);
                                return index === -1 ? VS.immediatelyError(Tr("choose_at_least_one_role")) : VS.any();
                            })
                        })}
                        onSubmit={onSubmit}>
                        {(formikProps) => (<>

                            <GridBlock justify="space-between">

                                <OrganizationInput
                                    formikProps={formikProps}
                                    id="organization"
                                    availableOrganizations={availableOrganizations}
                                    disabled={registration.emailAlreadyUsed || !registration.editable || iconWorks.anyWorks}
                                    required
                                />
                                <Button disabled={registration.emailAlreadyUsed || !registration.editable || iconWorks.anyWorks} onClick={() => setAddOrgPopup(true)}>{Tr("add_organization_unit")}</Button>
                            </GridBlock>

                            <PartsTable
                                formikProps={formikProps}
                                id="roles"
                                label={Tr('system_role')}
                                elementList={registration.availableRoles.map(r => ({identity: "_" + r.identity, name: r.name}))}
                                required={false}
                                disabled={registration.emailAlreadyUsed || !registration.editable || iconWorks.anyWorks}
                            />

                            {Object.keys(registration.availableAttributes).length > 0 &&
                                <>
                                    <BoldTypography variant="h6">{Tr("attributes")}</BoldTypography>
                                    <div className={classes.attributes}>
                                        {
                                            Object.keys(registration.availableAttributes).map(a => (

                                                <PartsTable
                                                    key={a}
                                                    formikProps={formikProps}
                                                    id={a}
                                                    label={a}
                                                    elementList={registration.availableAttributes[a].map(r => ({identity: r, name: r}))}
                                                    required={false}
                                                    disabled={registration.emailAlreadyUsed || !registration.editable || iconWorks.anyWorks}
                                                />
                                            ))
                                        }
                                    </div>

                                </>}
                            <MultiButton className={classes.backGrid} buttons={getButtons()}/>
                            {addOrgPopup && <UserRegistrationAddOrganizationUnit registrationId={regId}  onCreate={(val) => selectOrganization(formikProps, val)} onCancel={() => setAddOrgPopup(false)}/>}
                        </>)}
                    </Form>
                    {rejectPopup && <UserRegistrationRejectPopup onReject={doReject} onClose={() => setRejectPopup(false)}/>}
                </>
            }
        </Workspace>
    </Page>)
}

UserRegistration.propTypes = {
    classes: PropTypes.any,
}

export default connect((state) => ({formSubmit: state.form}), {})(UserRegistration);